import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { CurrencyCode, CustomColumnComponent, DataService, SharedModule } from '@vendure/admin-ui/core';
import {
    CustomizableImageDownloadResult,
    DisplayableCustomizableAsset,
    DisplayPaidOption,
    DisplayTextFrameGroup,
} from '../common/ui-types';
import { CREATE_CUSTOMIZABLE_IMAGE_DOWNLOAD_URLS, GET_ORDERLINE_CUSTOMIZATION_OPTION_NAME, GET_ORDERLINE_ORDERED_ASSET_IDS, GET_CUSTOMIZABLE_ASSETS_BUNDLE_URL, REGENERATE_CUSTOMIZABLE_ASSET_BUNDLE } from '../common/graphql';
import { OrderedSlot, sortSlotsByOrderedIds } from '../common/order-customizable-assets';

type OrderedItem =
    | { kind: 'asset'; data: DisplayableCustomizableAsset }
    | { kind: 'paid';  data: DisplayPaidOption }
    | { kind: 'text-frame-group'; data: DisplayTextFrameGroup };

/**
 * Admin-UI mirror of `formatTextFrameGroupDisplayName()` in
 * `src/plugins/email-notifications/helper.ts`. Kept lexically identical so both
 * surfaces render the same string.
 */
export function formatTextFrameGroupDisplayName(
    groupName: string,
    imageFrameName: string | undefined | null,
): string {
    // Coerce with String(): config is arbitrary JSON from the Shop API, so a
    // non-string groupName/imageFrameName must not throw on .trim().
    const trimmedFrame = String(imageFrameName ?? '').trim();
    const trimmedGroup = String(groupName ?? '').trim();
    if (trimmedFrame.length === 0) return trimmedGroup;
    return `${trimmedGroup} (${trimmedFrame})`;
}

@Component({
    selector: 'orderline-customization-data',
    template: `
        <div *ngIf="orderedItems.length; else none" class="assets-scroll">
            <div *ngIf="printTemplateName" class="mb-2 mt-2">
                {{printTemplateName}}
            </div>
            <ng-container *ngFor="let item of orderedItems">
                <!-- IMAGE ASSET -->
                <div *ngIf="item.kind === 'asset' && (item.data.type === 'image' || item.data.type === 'preview-container')" class="asset-row">
                    <img
                        [src]="item.data.previewUrl"
                        alt="Preview"
                        class="asset-image"
                    />
                    <div class="asset-content">
                        <span class="asset-text">{{ item.data.text }}</span>
                        <span class="asset-text">{{ item.data.name }}</span>
                        <a  [href]="item.data.downloadUrl"
                            [attr.download]="item.data.name"
                            target="_blank"
                            rel="noopener"
                            class="download-link">
                            Download
                        </a>
                    </div>
                </div>
                <!-- TEXT ASSET -->
                <div *ngIf="item.kind === 'asset' && item.data.type === 'text'" class="asset-row">
                    <div class="asset-text-content">
                        <span class="asset-text">{{ item.data.text }} {{ item.data.required?'*':''}}</span>
                        <span class="asset-text-block">{{ item.data.content }}</span>
                    </div>
                </div>
                <!-- PAID OPTION -->
                <div *ngIf="item.kind === 'paid'" class="paid-option-row">
                    <div class="paid-option-label"
                        [title]="item.data.content + ' <' + item.data.key + '>'">
                    <strong>{{ item.data.name }}</strong><strong *ngIf="item.data.required">*</strong>: {{ item.data.content }}&lt;{{ item.data.key }}&gt;
                    </div>
                    <div *ngIf="item.data.amount" class="paid-option-amount">
                        + {{ item.data.amount | localeCurrency: currencyCode }}
                    </div>
                </div>
                <!-- TEXT-FRAME GROUP -->
                <div *ngIf="item.kind === 'text-frame-group'" class="tf-group">
                    <div class="tf-group-header">
                        <strong>{{ item.data.displayName || item.data.groupName }}</strong>
                    </div>
                    <div class="tf-frame-row">
                        <span class="tf-frame-name">Font:</span>
                        <span class="tf-frame-content">{{ item.data.fontFamily }}</span>
                    </div>
                    <div class="tf-frame-row">
                        <span class="tf-frame-name">Text Color:</span>
                        <span class="tf-frame-content">{{ item.data.colorName }}</span>
                    </div>
                    <div *ngFor="let f of item.data.frames" class="tf-frame-row">
                        <span class="tf-frame-name">{{ f.name }}:</span>
                        <span class="tf-frame-content">{{ f.content }}</span>
                    </div>
                </div>
            </ng-container>

            <!-- total footer -->
            <div *ngIf="totalAddOn" class="paid-option-total">
                <span>Add-ons&nbsp;total</span>
                <span class="total-amount">
                {{ totalAddOn | localeCurrency: currencyCode }}
                </span>
            </div>

            <!-- zip bundle download -->
            <div *ngIf="bundleStatus === 'ready'" class="bundle-download">
                <a [href]="bundleDownloadUrl" target="_blank" rel="noopener" class="download-link">
                    Download All (ZIP)
                </a>
            </div>
            <div *ngIf="bundleStatus === 'pending'" class="bundle-download">
                <span class="bundle-pending">ZIP generating… refresh page to check.</span>
            </div>
            <div *ngIf="bundleStatus === 'unavailable'" class="bundle-download">
                <span *ngIf="!bundleRegenerating" class="bundle-pending">
                    ZIP not available.
                    <a href="javascript:void(0)" (click)="regenerateBundle()" class="download-link">Regenerate</a>
                </span>
                <span *ngIf="bundleRegenerating" class="bundle-pending">
                    ZIP generation queued. Refresh page to check.
                </span>
            </div>
        </div>

        <ng-template #none>
            <span>n/a</span>
        </ng-template>
    `,
    styles: [`
        /* make only 3 items visible, scroll for more */
        .assets-scroll {
            max-width: 300px;
            max-height: calc( (64px + 16px) * 3 ); /* image height + bottom margin, times 3 */
            overflow-y: auto;
            overflow-x: auto;
        }

        /* container for each asset row */
        .asset-row {
            display: flex;
            align-items: flex-start;
            margin-bottom: 4px;
        }

        .asset-row:not(:last-child) {
            border-bottom: 1px solid #e5e7eb;
            padding-bottom: 4px;
            margin-bottom: 4px;
        }

        /* the preview image on the left */
        .asset-image {
            width: 64px;
            height: 64px;
            object-fit: contain;
            border-radius: 4px;
            margin-right: 8px;
        }

        /* right-hand column holding text + link */
        .asset-content {
            display: flex;
            flex-direction: column;
            justify-content: center;
        }

        /* common text styling */
        .asset-text {
            margin-bottom: 4px;
            font-size: 14px;
            color: #333;
        }

        /* download link styling */
        .download-link {
            text-decoration: underline;
            font-size: 14px;
            color: #007bff;
        }

        .download-link:hover {
            color: #0056b3;
        }

        .asset-text-block {
            display: flex;
            flex-direction: column;
            max-height: 100px;
            min-height: 18px;
            margin-top: 4px;
            margin-bottom: 4px;
            overflow-y: auto;
        }

        .asset-text-content {
            margin-top: 4px;
            font-size: 14px;
            color: #666;
        }

        /* text-frame group styling */
        .tf-group {
            margin-top: 6px;
            padding: 6px;
            border: 1px solid #e5e7eb;
            border-radius: 4px;
            background: #fafafa;
        }

        .tf-group:not(:last-child) {
            margin-bottom: 4px;
        }

        .tf-group-header {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 13px;
            color: #333;
            margin-bottom: 4px;
        }

        .tf-font-label {
            font-size: 12px;
            color: #888;
        }

        .tf-color-dot {
            display: inline-block;
            width: 12px;
            height: 12px;
            border-radius: 50%;
            border: 1px solid #ccc;
        }

        .tf-color-value {
            font-size: 11px;
            color: #888;
            font-family: monospace;
        }

        .tf-frame-row {
            padding-left: 8px;
            font-size: 13px;
            color: #555;
            margin-bottom: 2px;
        }

        .tf-frame-name {
            font-weight: 600;
            margin-right: 4px;
        }

        /* Preserve newlines in multi-line text-overlay content (collapses runs of
           spaces but keeps the customer's line breaks). Without this the inline
           span renders all lines as one. */
        .tf-frame-content {
            white-space: pre-line;
        }

        .paid-options-wrapper {
            padding-top: 4px;
            margin-top: 4px;
            font-size: 14px;
            color: #666;
        }

        .paid-option-row {
            display: flex;
            flex-direction: column;
            padding: 6px 0;
            border-bottom: 1px solid #e5e7eb;
        }

        .paid-option-row:last-child {
            border-bottom: none;
        }

        .paid-option-label {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .paid-option-amount {
            text-align: right;
            font-weight: 600;
            color: #0d6efd;
            margin-top: 2px;
        }

        .paid-option-total {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-top: 6px;
            border-top: 1px dashed #c5c5c5;
            margin-top: 6px;
        }

        .total-amount {
            font-weight: 700;
            color: #0b47a1;
        }

        .bundle-download {
            margin-top: 6px;
            padding-top: 6px;
            border-top: 1px solid #e5e7eb;
        }

        .bundle-pending {
            font-size: 13px;
            color: #888;
        }
    `],
    standalone: true,
    imports: [SharedModule],
})
export class OrderLineCustomizableAssetsComponent implements CustomColumnComponent, OnInit {
    @Input() rowItem: any;

    totalAddOn = 0;
    currencyCode: CurrencyCode;
    printTemplateName: string | null = null;
    bundleDownloadUrl: string | null = null;
    bundleStatus: 'ready' | 'pending' | 'unavailable' | null = null;
    bundleRegenerating = false;
    // Slots are reserved up-front (null) and filled in place so async image-URL
    // fetches and bucketed text-frame groups keep their position. Each slot
    // carries the source asset id(s) so display order can be (re)applied in the
    // getter once the ordered-id sort hint resolves — rows stay visible even if
    // that query is slow or fails.
    private slots: Array<OrderedSlot<OrderedItem>> = [];
    // Settings-order sort hint from getOrderLineOrderedAssetIds. Starts empty so
    // the getter falls back to numeric id order until (if) the query succeeds.
    private orderedIds: string[] = [];
    // Memoised result of the slot sort. Recomputed only when `slots` or
    // `orderedIds` change (build, async image fill, ordered-ids resolve) — not on
    // every change-detection cycle.
    private cachedOrderedItems: OrderedItem[] = [];

    constructor(
        private dataService: DataService,
        private changeDetector: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.dataService.settings.getActiveChannel().single$.subscribe(data => {
            this.currencyCode = data.activeChannel.defaultCurrencyCode;
        });
        const customizableAssets = this.rowItem.customFields?.customizableAssets;
        if (customizableAssets?.length) {
            this.dataService.query(GET_CUSTOMIZABLE_ASSETS_BUNDLE_URL, {
                orderLineId: this.rowItem.id,
            }).single$.subscribe((res: any) => {
                const bundle = res.getCustomizableAssetsBundleUrl;
                this.bundleDownloadUrl = bundle?.url || null;
                this.bundleStatus = bundle?.status || null;
                this.changeDetector.markForCheck();
            });
        }
        this.dataService.query(GET_ORDERLINE_CUSTOMIZATION_OPTION_NAME, {
            orderLineId: this.rowItem.id,
        }).single$.subscribe((res: any) => {
            this.printTemplateName = res.getOrderLineCustomizationOptionName || null;
            this.changeDetector.markForCheck();
        });
        // Build rows immediately so customization data is visible regardless of
        // the sort-only ordered-ids query below.
        if (customizableAssets?.length) {
            this.buildItems(customizableAssets);
        }
        // The ordered-ids query only supplies settings-order sorting, applied
        // reactively in the orderedItems getter. A transient failure must NOT
        // hide the already-built rows — keep the numeric-id fallback order.
        this.dataService.query(GET_ORDERLINE_ORDERED_ASSET_IDS, {
            orderLineId: this.rowItem.id,
        }).single$.subscribe({
            next: (res: any) => {
                this.orderedIds = res.getOrderLineOrderedAssetIds || [];
                this.recomputeOrderedItems();
                this.changeDetector.markForCheck();
            },
            error: () => {
                // Leave rows in fallback id order; nothing else to do.
                this.changeDetector.markForCheck();
            },
        });
    }

    /**
     * Build the display list from `customizableAssets`. Slots are created in the
     * assets' natural order and each records the source asset id(s) it represents;
     * the settings-order sort (from getOrderLineOrderedAssetIds) is applied later
     * in the orderedItems getter. We reserve each item's slot up-front so async
     * image-URL fetches and bucketed text-frame groups land at a stable position,
     * and so the rows render before — and independently of — the sort query.
     */
    private buildItems(customizableAssets: any[]): void {
        const slots: Array<OrderedSlot<OrderedItem>> = [];
        const tfGroupMap = new Map<string, { slot: number; group: DisplayTextFrameGroup }>();
        let addOn = 0;

        for (const asset of customizableAssets) {
            if (asset.type === 'image' || asset.type === 'preview-container') {
                const slot = slots.length;
                slots.push({ sortIds: [String(asset.id)], value: null }); // filled when the download URL resolves
                this.dataService
                    .mutate(CREATE_CUSTOMIZABLE_IMAGE_DOWNLOAD_URLS, {
                        input: { customizableAssetId: asset.id },
                    })
                    .subscribe((res: any) => {
                        const urls = res.createCustomizableImageDownloadUrls as CustomizableImageDownloadResult;
                        slots[slot].value = {
                            kind: 'asset',
                            data: {
                                id: asset.id.toString(),
                                name: asset.name,
                                type: asset.type,
                                text: asset.text,
                                previewUrl: urls.thumbnailImageDownloadUrl ? urls.thumbnailImageDownloadUrl : urls.imageDownloadUrl,
                                downloadUrl: urls.imageDownloadUrl,
                            },
                        };
                        this.recomputeOrderedItems();
                        this.changeDetector.markForCheck();
                    });
            } else if (asset.type === 'text') {
                const cfg = asset.config as { label?: string; content?: string; required?: boolean } | null;
                slots.push({
                    sortIds: [String(asset.id)],
                    value: {
                        kind: 'asset',
                        data: {
                            id: asset.id.toString(),
                            name: asset.name,
                            type: 'text',
                            text: cfg?.label ?? asset.name,
                            content: cfg?.content ?? asset.text ?? '',
                            required: cfg?.required,
                        },
                    },
                });
            } else if (asset.type === 'text-frame') {
                const cfg = asset.config as { groupName?: string; imageFrameName?: string; content?: string; fontFamily?: string; color?: string; colorName?: string } | null;
                const optId = asset.productCustomizationOptionId ?? asset.productCustomizationOption?.id ?? '';
                // Composite identity (optionId, imageFrameName, groupName, font,
                // color) prevents same-named overlay groups across different image
                // frames from collapsing into one display bucket.
                const groupName = cfg?.groupName ?? asset.name ?? '';
                const imageFrameName = cfg?.imageFrameName ?? undefined;
                const key = `${optId}::${imageFrameName ?? ''}::${groupName}::${cfg?.fontFamily ?? ''}::${cfg?.color ?? ''}`;
                let entry = tfGroupMap.get(key);
                if (!entry) {
                    const slot = slots.length;
                    slots.push({ sortIds: [], value: null }); // reserve the group's position at first sight
                    entry = {
                        slot,
                        group: {
                            id: asset.id.toString(),
                            groupName,
                            imageFrameName,
                            displayName: formatTextFrameGroupDisplayName(groupName, imageFrameName),
                            fontFamily: cfg?.fontFamily ?? '',
                            color: cfg?.color ?? '',
                            colorName: cfg?.colorName || cfg?.color || '',
                            frames: [],
                        },
                    };
                    tfGroupMap.set(key, entry);
                }
                // A group sorts to the earliest settings-order position among its
                // member assets, matching the old build-time sort behaviour.
                slots[entry.slot].sortIds.push(String(asset.id));
                entry.group.frames.push({
                    name: asset.name ?? '',
                    content: cfg?.content ?? asset.text ?? '',
                });
            } else if (asset.type === 'paid-option') {
                const cfg = asset.config as { label?: string; content?: string; key?: string; required?: boolean } | null;
                if (cfg != null) {
                    slots.push({
                        sortIds: [String(asset.id)],
                        value: {
                            kind: 'paid',
                            data: {
                                id: asset.id.toString(),
                                name: asset.name ?? cfg?.label,
                                content: cfg?.content ?? cfg?.label ?? '',
                                key: cfg?.key,
                                amount: asset.amount,
                                required: cfg?.required,
                            },
                        },
                    });
                    addOn += asset.amount;
                }
            }
        }

        for (const { slot, group } of tfGroupMap.values()) {
            slots[slot].value = { kind: 'text-frame-group', data: group };
        }
        this.slots = slots;
        this.totalAddOn = addOn;
        this.recomputeOrderedItems();
    }

    get orderedItems(): OrderedItem[] {
        return this.cachedOrderedItems;
    }

    /**
     * Recompute the memoised, settings-order-sorted display list. Call after any
     * change to `slots` (build, async image-URL fill) or `orderedIds` (sort hint
     * resolves) — not from the getter, which runs on every change-detection cycle.
     *
     * While orderedIds is empty (query pending or failed) every slot maps to
     * Infinity, so rows fall back to numeric id order. Image slots may still be
     * null while their download URLs load, so drop the not-yet-filled holes.
     */
    private recomputeOrderedItems(): void {
        this.cachedOrderedItems = sortSlotsByOrderedIds(this.slots, this.orderedIds);
    }

    regenerateBundle(): void {
        this.bundleRegenerating = true;
        this.changeDetector.markForCheck();
        this.dataService.mutate(REGENERATE_CUSTOMIZABLE_ASSET_BUNDLE, {
            orderLineId: this.rowItem.id,
        }).subscribe((res: any) => {
            const status = res.regenerateCustomizableAssetBundle;
            if (status === 'pending') {
                this.bundleStatus = 'pending';
            }
            this.changeDetector.markForCheck();
        });
    }
}
